'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAccess } from '@repo/auth';
import { useDotEnv } from '@repo/dot-env/client';
import { CompanyRiskBanner, CompanyRiskBannerSkeleton } from '@context/companies';
import { permissions } from '@repo/permissions';
import { useSuspenseQuery } from '@repo/tanstack/query';
import { Suspense, useState } from 'react';
import { CreatedCustomerSetupLinkDialog, useCustomerSetupLinkActions } from '@/app/@protected/_components/customer-setup-links';
import { isTargetedCustomerSetupLinkEligible } from '@/app/@protected/_utils/customer-setup-links';
import { mapCompanyRiskBanner } from './map-company-risk-banner';
type NewLoadCompanyRiskBannerContentProps = {
  className?: string;
  companyId: number;
};
const NewLoadCompanyRiskBannerContent = t0 => {
  const $ = _c(30);
  const {
    className,
    companyId
  } = t0;
  const api = useApi();
  const env = useDotEnv();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      permissions: [permissions.customerSetupForms.tokens.create]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allowed: canCreateSetupLink
  } = useAccess(t1);
  let t2;
  if ($[1] !== api.companies.getDetails || $[2] !== companyId) {
    t2 = api.companies.getDetails.queryOptions({
      path: {
        id: companyId
      }
    });
    $[1] = api.companies.getDetails;
    $[2] = companyId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: company
  } = useSuspenseQuery(t2);
  let t3;
  if ($[4] !== companyId) {
    t3 = {
      companyId
    };
    $[4] = companyId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    createLink,
    isPending
  } = useCustomerSetupLinkActions(t3);
  const [createdSetupLinkUrl, setCreatedSetupLinkUrl] = useState();
  const [setupFormLinkError, setSetupFormLinkError] = useState();
  let t4;
  if ($[6] !== createLink) {
    t4 = async () => {
      setSetupFormLinkError(undefined);
      const result = await createLink();
      if (result.status === "error") {
        setSetupFormLinkError(result.message);
        return;
      }
      if (result.status === "created") {
        setCreatedSetupLinkUrl(result.url);
      }
    };
    $[6] = createLink;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCreateSetupFormLink = t4;
  let t5;
  if ($[8] !== company) {
    t5 = mapCompanyRiskBanner(company);
    $[8] = company;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const banner = t5;
  let t6;
  if ($[10] !== banner.status || $[11] !== canCreateSetupLink || $[12] !== company || $[13] !== env) {
    t6 = banner.status === "unverified-new-company" && isTargetedCustomerSetupLinkEligible({
      canCreate: canCreateSetupLink,
      customerSetupFormUrl: env.CUSTOMER_SETUP_FORM_URL,
      organisationSlug: company.organisation.slug,
      verifiedAt: company.verified_at
    });
    $[10] = banner.status;
    $[11] = canCreateSetupLink;
    $[12] = company;
    $[13] = env;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const canCreateCustomerSetupLink = t6;
  let t7;
  if ($[15] !== canCreateCustomerSetupLink || $[16] !== handleCreateSetupFormLink) {
    t7 = canCreateCustomerSetupLink ? () => void handleCreateSetupFormLink() : undefined;
    $[15] = canCreateCustomerSetupLink;
    $[16] = handleCreateSetupFormLink;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = isPending ? "creating" : "idle";
  let t9;
  if ($[18] !== banner || $[19] !== className || $[20] !== setupFormLinkError || $[21] !== t7 || $[22] !== t8) {
    t9 = <CompanyRiskBanner {...banner} className={className} onCreateSetupFormLink={t7} setupFormLinkError={setupFormLinkError} setupFormLinkStatus={t8} data-sentry-element="CompanyRiskBanner" data-sentry-source-file="company-risk-banner.tsx" />;
    $[18] = banner;
    $[19] = className;
    $[20] = setupFormLinkError;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setCreatedSetupLinkUrl(undefined);
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== createdSetupLinkUrl) {
    t11 = <CreatedCustomerSetupLinkDialog onClose={t10} url={createdSetupLinkUrl} data-sentry-element="CreatedCustomerSetupLinkDialog" data-sentry-source-file="company-risk-banner.tsx" />;
    $[25] = createdSetupLinkUrl;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t9) {
    t12 = <>{t9}{t11}</>;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
export const NewLoadCompanyRiskBanner = t0 => {
  const $ = _c(8);
  const {
    className,
    companyId
  } = t0;
  if (!companyId) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = <CompanyRiskBannerSkeleton className={className} />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== companyId) {
    t2 = <NewLoadCompanyRiskBannerContent className={className} companyId={companyId} key={companyId} data-sentry-element="NewLoadCompanyRiskBannerContent" data-sentry-source-file="company-risk-banner.tsx" />;
    $[2] = className;
    $[3] = companyId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Suspense fallback={t1} data-sentry-element="Suspense" data-sentry-component="NewLoadCompanyRiskBanner" data-sentry-source-file="company-risk-banner.tsx">{t2}</Suspense>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};